import api from './api';

// ── Mappers ─────────────────────────────────────────────

/**
 * Map API user (snake_case) → Frontend user (camelCase)
 */
function mapUser(u) {
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    cpf: u.cpf,
    phone: u.phone,
    birthDate: u.birth_date,
    status: u.status,
    role: u.role,
    photoUrl: u.photo_url,
    createdAt: u.created_at,
  };
}

/**
 * Map Frontend user (camelCase) → API user (snake_case)
 */
function mapUserToApi(data) {
  const mapped = {
    name: data.name,
    email: data.email,
    cpf: data.cpf,
    phone: data.phone,
    birth_date: data.birthDate,
    role: data.role,
    status: data.status,
  };

  if (data.password) {
    mapped.password = data.password;
    mapped.password_confirmation = data.passwordConfirmation || data.password_confirmation;
  }

  // Remove undefined values
  Object.keys(mapped).forEach((key) => {
    if (mapped[key] === undefined) delete mapped[key];
  });

  return mapped;
}

// ── Service functions ───────────────────────────────────

/**
 * Get all users with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise} { data, total, page, pageSize }
 */
export const getUsers = async (params = {}) => {
  try {
    const response = await api.get('/users', { params });
    const { data, meta } = response.data;

    return {
      data: data.map(mapUser),
      total: meta?.total || data.length,
      page: meta?.current_page || 1,
      pageSize: meta?.per_page || 10,
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar usuários.');
  }
};

/**
 * Get user by ID
 * @param {number} id
 * @returns {Promise} User data
 */
export const getUserById = async (id) => {
  try {
    const response = await api.get(`/users/${id}`);
    return mapUser(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao buscar usuário.');
  }
};

/**
 * Create new user
 * @param {Object} userData
 * @returns {Promise} Created user
 */
export const createUser = async (userData) => {
  try {
    const response = await api.post('/users', mapUserToApi(userData));
    return mapUser(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao criar usuário.');
  }
};

/**
 * Update user
 * @param {number} id
 * @param {Object} userData
 * @returns {Promise} Updated user
 */
export const updateUser = async (id, userData) => {
  try {
    const response = await api.put(`/users/${id}`, mapUserToApi(userData));
    return mapUser(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao atualizar usuário.');
  }
};

/**
 * Upload user photo
 * @param {number} id
 * @param {File} file - A imagem a ser enviada
 * @returns {Promise} Updated user
 */
export const uploadUserPhoto = async (id, file) => {
  try {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await api.post(`/users/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return mapUser(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao enviar foto do usuário.');
  }
};

/**
 * Delete user
 * @param {number} id
 * @returns {Promise}
 */
export const deleteUser = async (id) => {
  try {
    await api.delete(`/users/${id}`);
    return { success: true };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao deletar usuário.');
  }
};

/**
 * Toggle user status (active ↔ inactive)
 * @param {number} id
 * @returns {Promise} Updated user
 */
export const toggleUserStatus = async (id) => {
  try {
    // Fetch current user to determine new status
    const current = await getUserById(id);
    const newStatus = current.status === 'active' ? 'inactive' : 'active';

    const response = await api.patch(`/users/${id}/status`, { status: newStatus });
    return mapUser(response.data.data);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erro ao alterar status do usuário.');
  }
};
